import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import SetPassword from './pages/SetPassword';
import Layout from './components/Layout';

// Partner (admin) pages
import AdminDashboard from './pages/AdminDashboard';
import AdminCoaches from './pages/AdminCoaches';
import AdminUsers from './pages/AdminUsers';
import SplitTiers from './pages/SplitTiers';

// Shared business pages
import CoachDashboard from './pages/CoachDashboard';
import CoachProducts from './pages/CoachProducts';
import CoachSubscriptions from './pages/CoachSubscriptions';
import Clients from './pages/Clients';
import Transactions from './pages/Transactions';
import AccountSettings from './pages/AccountSettings';

// Senior coach pages
import MyTeam from './pages/MyTeam';
import AllClients from './pages/AllClients';
import Reporting from './pages/Reporting';
import PaymentSetup from './pages/PaymentSetup';
import ClientDashboard from './pages/ClientDashboard';

import './index.css';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (!user && location.pathname !== '/set-password') return <Navigate to="/login" replace />;
  return children;
};

// Route to correct dashboard based on role
const DashboardRouter = () => {
  const { profile, isAdmin, isSeniorCoach, loading } = useAuth();
  if (loading || !profile) return <LoadingScreen />;
  if (isAdmin) return <Navigate to="/partner" replace />;
  if (isSeniorCoach) return <Navigate to="/senior/team" replace />;
  if (profile.role === 'client') return <Navigate to="/my-account" replace />;
  return <Navigate to="/coach" replace />;
};

export const LoadingScreen = () => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '100vh', background: '#0a0a0a', color: '#e8d5b7',
    fontFamily: "'DM Sans', sans-serif"
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: 48, height: 48, border: '3px solid #2a2a2a',
        borderTop: '3px solid #e8d5b7', borderRadius: '50%',
        animation: 'spin 1s linear infinite', margin: '0 auto 16px'
      }} />
      Loading HMA Portal...
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<Login />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/dashboard" element={<PrivateRoute><DashboardRouter /></PrivateRoute>} />

          {/* Partner routes — full access */}
          <Route path="/partner" element={<PrivateRoute><Layout /></PrivateRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="coaches" element={<AdminCoaches />} />
            <Route path="admins" element={<AdminUsers />} />
            <Route path="all-clients" element={<AllClients />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="split-tiers" element={<SplitTiers />} />
            {/* Partner's own business */}
            <Route path="my-business" element={<CoachDashboard />} />
            <Route path="my-business/products" element={<CoachProducts />} />
            <Route path="my-business/subscriptions" element={<CoachSubscriptions />} />
            <Route path="my-business/clients" element={<Clients />} />
            <Route path="my-business/transactions" element={<Transactions />} />
            <Route path="my-business/reporting" element={<Reporting />} />
            <Route path="settings" element={<AccountSettings />} />
            {/* Redirect /partner/account → /partner/settings */}
            <Route path="account" element={<Navigate to="/partner/settings" replace />} />
            <Route path="payment-setup" element={<PaymentSetup />} />
          </Route>

          {/* Senior Coach routes */}
          <Route path="/senior" element={<PrivateRoute><Layout /></PrivateRoute>}>
            <Route path="team" element={<MyTeam />} />
            <Route path="my-business" element={<CoachDashboard />} />
            <Route path="my-business/products" element={<CoachProducts />} />
            <Route path="my-business/subscriptions" element={<CoachSubscriptions />} />
            <Route path="my-business/clients" element={<Clients />} />
            <Route path="my-business/transactions" element={<Transactions />} />
            <Route path="my-business/reporting" element={<Reporting />} />
            <Route path="settings" element={<AccountSettings />} />
            {/* Redirect /senior/account → /senior/settings */}
            <Route path="account" element={<Navigate to="/senior/settings" replace />} />
            <Route path="payment-setup" element={<PaymentSetup />} />
          </Route>

          {/* Coach routes */}
          <Route path="/coach" element={<PrivateRoute><Layout /></PrivateRoute>}>
            <Route index element={<CoachDashboard />} />
            <Route path="products" element={<CoachProducts />} />
            <Route path="subscriptions" element={<CoachSubscriptions />} />
            <Route path="clients" element={<Clients />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="reporting" element={<Reporting />} />
            <Route path="settings" element={<AccountSettings />} />
            {/* Redirect /coach/account → /coach/settings */}
            <Route path="account" element={<Navigate to="/coach/settings" replace />} />
            <Route path="payment-setup" element={<PaymentSetup />} />
          </Route>

          {/* Client portal */}
          <Route path="/my-account" element={<ClientDashboard />} />

          {/* Legacy redirects */}
          <Route path="/admin/*" element={<Navigate to="/partner" replace />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
